import { z } from "zod";
import type { Project } from "./schema";

export const projectPageQuerySchema = z
  .object({
    offset: z.coerce.number().int().min(0).max(1000000).default(0),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    status: z
      .enum(["active", "draft", "published", "failed", "archived", "all"])
      .default("active"),
    order: z.enum(["oldest", "newest"]).default("oldest"),
    search: z.string().trim().max(200).default(""),
  })
  .strict();
export type ProjectPageQuery = z.input<typeof projectPageQuerySchema>;
export interface ProjectPage<T = Project> {
  items: T[];
  total: number;
  offset: number;
  limit: number;
}
export type ProjectListItem = Pick<
  Project,
  "id" | "tenantId" | "name" | "description" | "status" | "revision" | "usedAt"
> & { fieldCount: number; outputCount: number };
export interface ProjectCatalogPage {
  items: ProjectListItem[];
  total: number;
  offset: number;
  limit: number;
}
export const summarizeProject = (project: Project): ProjectListItem => ({
  id: project.id,
  tenantId: project.tenantId,
  name: project.name,
  description: project.description,
  status: project.status,
  revision: project.revision,
  ...(project.usedAt ? { usedAt: project.usedAt } : {}),
  fieldCount: project.fields.length,
  outputCount: project.outputs.length,
});
export function matchesProjectStatus(
  project: Pick<Project, "id" | "status">,
  status: z.output<typeof projectPageQuerySchema>["status"],
) {
  const failed = project.id.startsWith("project_import_failed_");
  return (
    status === "all" ||
    (status === "active"
      ? project.status !== "archived"
      : status === "failed"
        ? failed && project.status !== "archived"
        : status === "draft"
          ? project.status === "draft" && !failed
          : project.status === status)
  );
}

/** Filter references before cloning, so paging never copies the whole workspace. */
export function paginateProjects<
  T extends Pick<Project, "id" | "name" | "description" | "status">,
>(projects: readonly T[], input: ProjectPageQuery = {}): ProjectPage<T> {
  const query = projectPageQuerySchema.parse(input);
  const search = query.search.toLowerCase();
  const matches = projects.filter(
    (project) =>
      matchesProjectStatus(project, query.status) &&
      (!search ||
        `${project.name} ${project.description}`
          .toLowerCase()
          .includes(search)),
  );
  if (query.order === "newest") matches.reverse();
  const offset = Math.min(
    query.offset,
    Math.max(0, Math.ceil(matches.length / query.limit) - 1) * query.limit,
  );
  return {
    items: structuredClone(matches.slice(offset, offset + query.limit)),
    total: matches.length,
    offset,
    limit: query.limit,
  };
}
